import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './Commonlibs/store';
import { logout } from './Commonlibs/userSlice';
import Navbar from './Commonlibs/Navbar';
import Login from './Authuntication/Login';
import Register from './Authuntication/Register';
import ForgotPassword from './Authuntication/ForgotPassword';
import ChangePassword from './Authuntication/ChangePassword';
import Main from './Buyer/components/main';
import PropertySearch from './Buyer/components/Property/PropertySearch';
import PropertyView from './Buyer/components/Property/PropertyView';
import TalkView from './Buyer/components/Property/TalkView';
import Loader from './Loader/Loader';
import SellerProperties from './Seller/SellerProperties';
import Footer from './Commonlibs/Footer';
import ProfileContent from './Buyer/components/Profile/ProfileContent';
import ProfilePropEdit from './Buyer/components/Profile/ProfilePropEdit';
import ProfilePropLiked from './Buyer/components/Profile/ProfilePropLiked';
import ProfilePropViewed from './Buyer/components/Profile/ProfilePropViewed';
import ProfileViewMain from './Buyer/components/Profile/ProfileViewMain';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import Buyplans from './Seller/Buyplans';
import AddNewProperty from './Seller/AddNewProperty';
import PropertyDetails from './Seller/PropertyDetails';
import PriceDetails from './Seller/PriceDetails';
import RentDetails from './Seller/RentDetails';
import PropertySeeAll from './Seller/PropertySeeAll';
import RecommendedView from './Buyer/components/Home/ResidentialView';
import FeaturedView from './Buyer/components/Home/FeaturedView';
import NewlyView from './Buyer/components/Home/NewlyView';
import EditProperties from './Seller/EditProperties';
import ProfileSearchMain from './Buyer/components/Profile/ProfileSearchMain';
import ResidentialView from './Buyer/components/Home/ResidentialView';
import Amenities from './Seller/Amenities';
import PostAccess from './Seller/PostAccess';
import { HelmetProvider } from 'react-helmet-async'; // ✅ ADD THIS

const theme = createTheme({
  typography: {
    fontFamily: 'Outfit, sans-serif',
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
});

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const hideNavbarPaths = ['/login', '/register', '/forgot-password'];
  const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);
  const shouldShowFooter = !hideNavbarPaths.includes(location.pathname);

  // ✅ Check authentication validity on mount
  useEffect(() => {
    const checkAuthValidity = () => {
      const token = localStorage.getItem('auth_token');
      const userId = localStorage.getItem('user_id');
      const browserSession = sessionStorage.getItem('browser_session_active');

      console.log('🔍 Auth check on mount:', { 
        token: !!token, 
        userId: !!userId, 
        browserSession: !!browserSession 
      });

      // ✅ If user has token but no browser session, browser was closed
      if (token && userId && !browserSession) {
        console.log('⚠️ Browser was closed - clearing auth');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        localStorage.removeItem('persist:root');
        dispatch(logout());
        persistor.purge();
        
        // Redirect to login if not already there
        if (!hideNavbarPaths.includes(location.pathname)) {
          navigate('/login');
        }
      }
      // If user has valid session, keep logged in
      else if (token && userId && browserSession) {
        console.log('✅ Valid browser session found - user still logged in');
      }
      // If no token but session flag exists, clean up
      else if (!token && browserSession) {
        console.log('⚠️ No token but session flag exists - cleaning up');
        sessionStorage.removeItem('browser_session_active');
        dispatch(logout());
        persistor.purge();
      }
    };

    checkAuthValidity();
  }, [dispatch, navigate, location.pathname]);

  return (
    <>
      {shouldShowNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<Main />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="/property" element={<PropertySearch />} />
        <Route path="/view/:propertyId" element={<TalkView />} />
        <Route path="/sell-properties" element={<SellerProperties />} />
        <Route path="/sell-properties/edit/:propertyId" element={<SellerProperties />} />
        <Route path="/sell-properties/inbox" element={<SellerProperties />} />
        <Route path="/sell-properties/transactions" element={<SellerProperties />} />
        <Route path="/sell-properties/seeAll" element={<SellerProperties />} />
        <Route path="/residential-view" element={<ResidentialView />} />
        <Route path="/featured-view" element={<FeaturedView />} />
        <Route path="/newly-view" element={<NewlyView />} />
        <Route path="/buy-plans" element={<Buyplans />} />
        <Route path="/add-new-property" element={<AddNewProperty />} />
        <Route path="/property-details" element={<PropertyDetails />} />
        <Route path="/price-details" element={<PriceDetails />} />
        <Route path="/amenities" element={<Amenities />} />
        <Route path="/rent-details" element={<RentDetails />} />
        <Route path="/profile" element={<ProfileContent />} />
        <Route path="/profile/view-main" element={<ProfileViewMain />} />
        <Route path="/profile/inbox" element={<ProfilePropViewed />} />
        <Route path="/profile/wishlist" element={<ProfilePropLiked />} />
        <Route path="/profile/recent-searches" element={<ProfileSearchMain />} />
        <Route path="/profile/edit" element={<ProfilePropEdit />} />
        <Route path="/sell-properties/edit/:propertyId" element={<EditProperties />} />
      </Routes>
      {shouldShowFooter && <Footer />}
    </>
  );
};

const App = () => {
  const [persistReady, setPersistReady] = useState(false);

  // ✅ CRITICAL FIX: Check browser session BEFORE allowing persist to rehydrate
  useEffect(() => {
    const checkPersistValidity = async () => {
      const token = localStorage.getItem('auth_token');
      const persistRoot = localStorage.getItem('persist:root');
      const browserSession = sessionStorage.getItem('browser_session_active');

      console.log('🔍 Checking persist validity BEFORE rehydration:', { 
        token: !!token, 
        persistRoot: !!persistRoot,
        browserSession: !!browserSession
      });

      // ✅ CRITICAL: If no browser session exists, clear EVERYTHING before PersistGate loads
      if (!browserSession && (token || persistRoot)) {
        console.log('⚠️ NO BROWSER SESSION - Clearing all auth data BEFORE rehydration');
        
        // Purge persistor first
        await persistor.purge();
        await persistor.flush();
        
        // Then clear localStorage
        localStorage.removeItem('persist:root');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        
        console.log('✅ All auth data cleared - user will be logged out');
      } else if (browserSession) {
        console.log('✅ Browser session active - allowing rehydration');
      }

      setPersistReady(true);
    };

    checkPersistValidity();
  }, []);

  if (!persistReady) {
    return <Loader />;
  }

  return (
    <Provider store={store}>
      <PersistGate loading={<Loader />} persistor={persistor}>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
        </HelmetProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;







// import React, { useState } from 'react';
// import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
// import { Provider } from 'react-redux';
// import { PersistGate } from 'redux-persist/integration/react';
// import { store, persistor } from './Commonlibs/store';
// import Navbar from './Commonlibs/Navbar';
// import Login from './Authuntication/Login';
// import Register from './Authuntication/Register';
// import ForgotPassword from './Authuntication/ForgotPassword';
// import ChangePassword from './Authuntication/ChangePassword';
// import Main from './Buyer/components/main';
// import PropertySearch from './Buyer/components/Property/PropertySearch';
// import PropertyView from './Buyer/components/Property/PropertyView';
// import TalkView from './Buyer/components/Property/TalkView';
// import Loader from './Loader/Loader';
// import SellerProperties from './Seller/SellerProperties';
// import Footer from './Commonlibs/Footer';
// import ProfileContent from './Buyer/components/Profile/ProfileContent';
// import ProfilePropEdit from './Buyer/components/Profile/ProfilePropEdit';
// import ProfilePropLiked from './Buyer/components/Profile/ProfilePropLiked';
// import ProfilePropViewed from './Buyer/components/Profile/ProfilePropViewed';
// import ProfileViewMain from './Buyer/components/Profile/ProfileViewMain';
// import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
// import Buyplans from './Seller/Buyplans';
// import AddNewProperty from './Seller/AddNewProperty';
// import PropertyDetails from './Seller/PropertyDetails';
// import PriceDetails from './Seller/PriceDetails';
// import RentDetails from './Seller/RentDetails';
// import PropertySeeAll from './Seller/PropertySeeAll';
// import RecommendedView from './Buyer/components/Home/ResidentialView';
// import FeaturedView from './Buyer/components/Home/FeaturedView';
// import NewlyView from './Buyer/components/Home/NewlyView';
// import EditProperties from './Seller/EditProperties';
// import ProfileSearchMain from './Buyer/components/Profile/ProfileSearchMain';
// import ResidentialView from './Buyer/components/Home/ResidentialView';
// import Amenities from './Seller/Amenities';
// import PostAccess from './Seller/PostAccess';

// // ⬇️ REPLACE YOUR OLD THEME WITH THIS ⬇️
// const theme = createTheme({
//   typography: {
//     fontFamily: 'Outfit, sans-serif',
//   },
//   palette: {
//     primary: {
//       main: '#1976d2',
//     },
//     background: {
//       default: '#f5f5f5',  // Light grey background
//       paper: '#ffffff',     // White for paper components
//     },
//   },
//   components: {
//     MuiCssBaseline: {
//       styleOverrides: {
//         body: {
//           backgroundColor: '#e6e2e2ff',
//         },
//       },
//     },
//   },
// });
// // ⬆️ END OF THEME CONFIGURATION ⬆️

// const AppContent = () => {
//   const location = useLocation();
//   const hideNavbarPaths = ['/login', '/register','/forgot-password'];
//   const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);
//   const shouldShowFooter = !hideNavbarPaths.includes(location.pathname);
  
//   return (
//     <>
//       {shouldShowNavbar && <Navbar />}
//       <Routes>
//         <Route path="/" element={<Main />} />
//         <Route path="/login" element={<Login />} />
//         <Route path="/register" element={<Register />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route path="change-password" element={<ChangePassword />} />
//         <Route path="/property" element={<PropertySearch />} />
//         <Route path="/view/:propertyId" element={<TalkView />} />
//         <Route path="/sell-properties" element={<SellerProperties />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<SellerProperties />} />
//         <Route path="/sell-properties/inbox" element={<SellerProperties />} />
//         <Route path="/sell-properties/transactions" element={<SellerProperties />} />
//         <Route path="/sell-properties/seeAll" element={<SellerProperties />} />
//         <Route path="/residential-view" element={<ResidentialView />} />
//         <Route path="/featured-view" element={<FeaturedView />} />
//         <Route path="/newly-view" element={<NewlyView />} />
//         <Route path="/buy-plans" element={<Buyplans />} />
//         <Route path="/add-new-property" element={<AddNewProperty />} />
//         <Route path="/property-details" element={<PropertyDetails />} />
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/amenities" element={<Amenities />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/profile" element={<ProfileContent />} />
//         <Route path="/profile/view-main" element={<ProfileViewMain />} />
//         <Route path="/profile/inbox" element={<ProfilePropViewed />} />
//         <Route path="/profile/wishlist" element={<ProfilePropLiked />} />
//         <Route path="/profile/recent-searches" element={<ProfileSearchMain />} />
//         <Route path="/profile/edit" element={<ProfilePropEdit />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<EditProperties />} />
//       </Routes>
//       {shouldShowFooter && <Footer />}
//     </>
//   );
// };

// const App = () => {
//   return (
//     <Provider store={store}>
//       <PersistGate loading={<Loader />} persistor={persistor}>
//         <ThemeProvider theme={theme}>
//           <CssBaseline />
//           <Router>
//             <AppContent />
//           </Router>
//         </ThemeProvider>
//       </PersistGate>
//     </Provider>
//   );
// };

// export default App;




// import React, { useState } from 'react';
// import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
// import { Provider } from 'react-redux';
// import { PersistGate } from 'redux-persist/integration/react';
// import { store, persistor } from './Commonlibs/store';
// import Navbar from './Commonlibs/Navbar';
// import Login from './Authuntication/Login';
// import Register from './Authuntication/Register';
// import ForgotPassword from './Authuntication/ForgotPassword';
// import ChangePassword from './Authuntication/ChangePassword';
// import Main from './Buyer/components/main';
// import PropertySearch from './Buyer/components/Property/PropertySearch';
// import PropertyView from './Buyer/components/Property/PropertyView';
// import TalkView from './Buyer/components/Property/TalkView'; // Import TalkView
// import Loader from './Loader/Loader';
// import SellerProperties from './Seller/SellerProperties';
// import Footer from './Commonlibs/Footer';
// import ProfileContent from './Buyer/components/Profile/ProfileContent';
// import ProfilePropEdit from './Buyer/components/Profile/ProfilePropEdit';
// import ProfilePropLiked from './Buyer/components/Profile/ProfilePropLiked';
// import ProfilePropViewed from './Buyer/components/Profile/ProfilePropViewed';
// import ProfileViewMain from './Buyer/components/Profile/ProfileViewMain';
// import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
// import Buyplans from './Seller/Buyplans';
// import AddNewProperty from './Seller/AddNewProperty';
// import PropertyDetails from './Seller/PropertyDetails';
// // import PropertyCommercial from './Seller/PropertyCommercial';
// import PriceDetails from './Seller/PriceDetails';
// import RentDetails from './Seller/RentDetails';
// import PropertySeeAll from './Seller/PropertySeeAll';
// import RecommendedView from './Buyer/components/Home/ResidentialView';
// import FeaturedView from './Buyer/components/Home/FeaturedView'; 
// import NewlyView from './Buyer/components/Home/NewlyView';
// import EditProperties from './Seller/EditProperties';
// import ProfileSearchMain from './Buyer/components/Profile/ProfileSearchMain';
// import ResidentialView from './Buyer/components/Home/ResidentialView';
// import Amenities from './Seller/Amenities';
// import PostAccess from './Seller/PostAccess';
// // Create MUI theme with Outfit
// const theme = createTheme({
//   typography: {
//     fontFamily: 'Outfit, sans-serif',
//   },
//   palette: {
//     primary: {
//       main: '#1976d2',
//     },
//   },
// });
 
// const AppContent = () => {
//   const location = useLocation();
//   const hideNavbarPaths = ['/login', '/register','/forgot-password'];
//   const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);
//   const shouldShowFooter = !hideNavbarPaths.includes(location.pathname);
 
//   return (
//     <>
//       {shouldShowNavbar && <Navbar />}
//       <Routes>
//         <Route path="/" element={<Main />} />
//         <Route path="/login" element={<Login />} />
//         <Route path="/register" element={<Register />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route path="change-password" element={<ChangePassword />} />
//         <Route path="/property" element={<PropertySearch />} />
//         {/* <Route path="/view" element={<PropertyView />} /> */}
//         <Route path="/view/:propertyId" element={<TalkView />} /> {/* Add route with ID parameter */}
//         <Route path="/sell-properties" element={<SellerProperties />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<SellerProperties />} />
//         <Route path="/sell-properties/inbox" element={<SellerProperties />} />
//         <Route path="/sell-properties/transactions" element={<SellerProperties />} />
//         <Route path="/sell-properties/seeAll" element={<SellerProperties />} />
//         <Route path="/residential-view" element={<ResidentialView />} />
//         <Route path="/featured-view" element={<FeaturedView />} />
//         <Route path="/newly-view" element={<NewlyView />} />
//         <Route path="/buy-plans" element={<Buyplans />} />
//         <Route path="/add-new-property" element={<AddNewProperty />} />
//         <Route path="/property-details" element={<PropertyDetails />} />
//         {/* <Route path="/property-commercial" element={<PropertyCommercial />} /> */}
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/amenities" element={<Amenities />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/profile" element={<ProfileContent />} />
//         <Route path="/profile/view-main" element={<ProfileViewMain />} />
//         <Route path="/profile/inbox" element={<ProfilePropViewed />} />
//         <Route path="/profile/wishlist" element={<ProfilePropLiked />} />
//         <Route path="/profile/recent-searches" element={<ProfileSearchMain />} />
//         <Route path="/profile/edit" element={<ProfilePropEdit />} />
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<EditProperties />} />
//       </Routes>
//       {shouldShowFooter && <Footer />}
//     </>
//   );
// };
 
// const App = () => {
//   return (
//     <Provider store={store}>
//       <PersistGate loading={<Loader />} persistor={persistor}>
//         <ThemeProvider theme={theme}>
//           <CssBaseline />
//           <Router>
//             <AppContent />
//           </Router>
//         </ThemeProvider>
//       </PersistGate>
//     </Provider>
//   );
// };
 
// export default App;








// import React, { useState } from 'react';
// import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
// import { Provider } from 'react-redux';
// import { PersistGate } from 'redux-persist/integration/react';
// import { store, persistor } from './Commonlibs/store';
// import Navbar from './Commonlibs/Navbar';
// import Login from './Authuntication/Login';
// import Register from './Authuntication/Register';
// import ForgotPassword from './Authuntication/ForgotPassword';
// import ChangePassword from './Authuntication/ChangePassword';
// import Main from './Buyer/components/main';
// import PropertySearch from './Buyer/components/Property/PropertySearch';
// import PropertyView from './Buyer/components/Property/PropertyView';
// import TalkView from './Buyer/components/Property/TalkView'; // Import TalkView
// import Loader from './Loader/Loader';
// import SellerProperties from './Seller/SellerProperties';
// import Footer from './Commonlibs/Footer';
// import ProfileContent from './Buyer/components/Profile/ProfileContent';
// import ProfilePropEdit from './Buyer/components/Profile/ProfilePropEdit';
// import ProfilePropLiked from './Buyer/components/Profile/ProfilePropLiked';
// import ProfilePropViewed from './Buyer/components/Profile/ProfilePropViewed';
// import ProfileViewMain from './Buyer/components/Profile/ProfileViewMain';
// import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
// import Buyplans from './Seller/Buyplans';
// import AddNewProperty from './Seller/AddNewProperty';
// import PropertyDetails from './Seller/PropertyDetails';
// // import PropertyCommercial from './Seller/PropertyCommercial';
// import PriceDetails from './Seller/PriceDetails';
// import RentDetails from './Seller/RentDetails';
// import PropertySeeAll from './Seller/PropertySeeAll';
// import RecommendedView from './Buyer/components/Home/ResidentialView';
// import FeaturedView from './Buyer/components/Home/FeaturedView'; 
// import NewlyView from './Buyer/components/Home/NewlyView';
// import EditProperties from './Seller/EditProperties';
// import ProfileSearchMain from './Buyer/components/Profile/ProfileSearchMain';
// import ResidentialView from './Buyer/components/Home/ResidentialView';
// // Create MUI theme with Outfit
// const theme = createTheme({
//   typography: {
//     fontFamily: 'Outfit, sans-serif',
//   },
//   palette: {
//     primary: {
//       main: '#1976d2',
//     },
//   },
// });
 
// const AppContent = () => {
//   const location = useLocation();
//   const hideNavbarPaths = ['/login', '/register','/forgot-password'];
//   const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);
//   const shouldShowFooter = !hideNavbarPaths.includes(location.pathname);
 
//   return (
//     <>
//       {shouldShowNavbar && <Navbar />}
//       <Routes>
//         <Route path="/" element={<Main />} />
//         <Route path="/login" element={<Login />} />
//         <Route path="/register" element={<Register />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route path="change-password" element={<ChangePassword />} />
//         <Route path="/property" element={<PropertySearch />} />
//         {/* <Route path="/view" element={<PropertyView />} /> */}
//         <Route path="/view/:propertyId" element={<TalkView />} /> {/* Add route with ID parameter */}
//         <Route path="/sell-properties" element={<SellerProperties />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<SellerProperties />} />
//         <Route path="/sell-properties/inbox" element={<SellerProperties />} />
//         <Route path="/sell-properties/transactions" element={<SellerProperties />} />
//         <Route path="/sell-properties/seeAll" element={<SellerProperties />} />
//         <Route path="/residential-view" element={<ResidentialView />} />
//         <Route path="/featured-view" element={<FeaturedView />} />
//         <Route path="/newly-view" element={<NewlyView />} />
//         <Route path="/buy-plans" element={<Buyplans />} />
//         <Route path="/add-new-property" element={<AddNewProperty />} />
//         <Route path="/property-details" element={<PropertyDetails />} />
//         {/* <Route path="/property-commercial" element={<PropertyCommercial />} /> */}
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/profile" element={<ProfileContent />} />
//         <Route path="/profile/view-main" element={<ProfileViewMain />} />
//         <Route path="/profile/inbox" element={<ProfilePropViewed />} />
//         <Route path="/profile/wishlist" element={<ProfilePropLiked />} />
//         <Route path="/profile/recent-searches" element={<ProfileSearchMain />} />
//         <Route path="/profile/edit" element={<ProfilePropEdit />} />
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<EditProperties />} />
//       </Routes>
//       {shouldShowFooter && <Footer />}
//     </>
//   );
// };
 
// const App = () => {
//   return (
//     <Provider store={store}>
//       <PersistGate loading={<Loader />} persistor={persistor}>
//         <ThemeProvider theme={theme}>
//           <CssBaseline />
//           <Router>
//             <AppContent />
//           </Router>
//         </ThemeProvider>
//       </PersistGate>
//     </Provider>
//   );
// };
 
// export default App;





// import React from 'react';
// import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// import { Provider } from 'react-redux';
// import { PersistGate } from 'redux-persist/integration/react';
// import { store, persistor } from './Commonlibs/store';
// import Navbar from './Commonlibs/Navbar';
// import Login from './Authuntication/Login';
// import Register from './Authuntication/Register';
// import ForgotPassword from './Authuntication/ForgotPassword';
// import ChangePassword from './Authuntication/ChangePassword';
// import Main from './Buyer/components/main';
// import PropertySearch from './Buyer/components/Property/PropertySearch';
// import PropertyView from './Buyer/components/Property/PropertyView';
// import TalkView from './Buyer/components/Property/TalkView';
// import Loader from './Loader/Loader';
// import SellerProperties from './Seller/SellerProperties';
// import Footer from './Commonlibs/Footer';
// import ProfileContent from './Buyer/components/Profile/ProfileContent';
// import ProfilePropEdit from './Buyer/components/Profile/ProfilePropEdit';
// import ProfilePropLiked from './Buyer/components/Profile/ProfilePropLiked';
// import ProfilePropViewed from './Buyer/components/Profile/ProfilePropViewed';
// import ProfileViewMain from './Buyer/components/Profile/ProfileViewMain';
// import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
// import Buyplans from './Seller/Buyplans';
// import AddNewProperty from './Seller/AddNewProperty';
// import PropertyDetails from './Seller/PropertyDetails';
// import PriceDetails from './Seller/PriceDetails';
// import RentDetails from './Seller/RentDetails';
// import PropertySeeAll from './Seller/PropertySeeAll';
// import RecommendedView from './Buyer/components/Home/ResidentialView';
// import FeaturedView from './Buyer/components/Home/FeaturedView';
// import NewlyView from './Buyer/components/Home/NewlyView';
// import EditProperties from './Seller/EditProperties';
// import ProfileSearchMain from './Buyer/components/Profile/ProfileSearchMain';
// import ResidentialView from './Buyer/components/Home/ResidentialView';
 
// // Import Global Ad Component
// import GlobalAd from './Buyer/components/Ads/GlobalAd';
 
// // Create MUI theme
// const theme = createTheme({
//   typography: {
//     fontFamily: 'Outfit, sans-serif',
//   },
//   palette: {
//     primary: {
//       main: '#1976d2',
//     },
//   },
// });
 
// const AppContent = () => {
//   const location = useLocation();
//   const hideNavbarPaths = ['/login', '/register', '/forgot-password'];
//   const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);
//   const shouldShowFooter = !hideNavbarPaths.includes(location.pathname);
 
//   return (
//     <>
//       {/* Global Ad - Shows on Top of Everything */}
//     <GlobalAd position="top-right" />
     
//       {shouldShowNavbar && <Navbar />}
     
//       <Routes>
//         <Route path="/" element={<Main />} />
//         <Route path="/login" element={<Login />} />
//         <Route path="/register" element={<Register />} />
//         <Route path="/forgot-password" element={<ForgotPassword />} />
//         <Route path="change-password" element={<ChangePassword />} />
//         <Route path="/property" element={<PropertySearch />} />
//         <Route path="/view/:propertyId" element={<TalkView />} />
//         <Route path="/sell-properties" element={<SellerProperties />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<SellerProperties />} />
//         <Route path="/sell-properties/inbox" element={<SellerProperties />} />
//         <Route path="/sell-properties/transactions" element={<SellerProperties />} />
//         <Route path="/sell-properties/seeAll" element={<SellerProperties />} />
//         <Route path="/residential-view" element={<ResidentialView />} />
//         <Route path="/featured-view" element={<FeaturedView />} />
//         <Route path="/newly-view" element={<NewlyView />} />
//         <Route path="/buy-plans" element={<Buyplans />} />
//         <Route path="/add-new-property" element={<AddNewProperty />} />
//         <Route path="/property-details" element={<PropertyDetails />} />
//         <Route path="/price-details" element={<PriceDetails />} />
//         <Route path="/rent-details" element={<RentDetails />} />
//         <Route path="/profile" element={<ProfileContent />} />
//         <Route path="/profile/view-main" element={<ProfileViewMain />} />
//         <Route path="/profile/inbox" element={<ProfilePropViewed />} />
//         <Route path="/profile/wishlist" element={<ProfilePropLiked />} />
//         <Route path="/profile/recent-searches" element={<ProfileSearchMain />} />
//         <Route path="/profile/edit" element={<ProfilePropEdit />} />
//         <Route path="/sell-properties/edit/:propertyId" element={<EditProperties />} />
//       </Routes>
 
//       {shouldShowFooter && <Footer />}
//     </>
//   );
// };
 
// const App = () => {
//   return (
//     <Provider store={store}>
//       <PersistGate loading={<Loader />} persistor={persistor}>
//         <ThemeProvider theme={theme}>
//           <CssBaseline />
//           <Router>
//             <AppContent />
//           </Router>
//         </ThemeProvider>
//       </PersistGate>
//     </Provider>
//   );
// };
 
// export default App;